/**
 * Feature registry — the central gate through which all gated features pass.
 *
 * The registry is a module-level singleton. Features are registered once at
 * app startup (see `features.ts`) and queried at render time via
 * `useFeatureAccess` or `getFeatureDefinition`.
 *
 * Usage:
 *   1. Call `registerFeature()` once per feature — typically in `features.ts`.
 *   2. Use `useFeatureAccess(key)` inside components to enforce access rules.
 */

import type { PricingTier } from './pricingTiers';

/** How a feature is exposed to users on a given pricing tier. */
export type FeatureAccess = 'full' | 'locked' | 'hidden';

/**
 * Defines a gated feature and its per-tier access level.
 *
 * Access modes:
 *   - `full`   — the feature is fully accessible.
 *   - `locked` — the feature is visible in the UI but blocked; interacting
 *                with it shows an upgrade prompt with a plan-comparison link.
 *   - `hidden` — the feature is not rendered in the UI at all.
 */
export interface FeatureDefinition {
    /** Unique string key used to look up the feature (e.g. `'ai_slicing'`). */
    key: string;

    /** Short human-readable description used in upgrade prompt messages. */
    description: string;

    /** Per-tier access map. Every defined `PricingTier` must have an entry. */
    access: Record<PricingTier, FeatureAccess>;
}

/** Internal registry — private to this module. */
const registry = new Map<string, FeatureDefinition>();

/**
 * Registers a feature with the pricing system.
 *
 * Call this once per feature at module load time (in `features.ts`).
 * Registering the same key twice overwrites the previous definition.
 */
export function registerFeature(feature: FeatureDefinition): void {
    registry.set(feature.key, feature);
}

/**
 * Looks up a registered feature by key.
 * Returns `undefined` if the feature has not been registered.
 */
export function getFeatureDefinition(key: string): FeatureDefinition | undefined {
    return registry.get(key);
}

/**
 * Clears the entire registry.
 * **For use in tests only** — do not call in production code.
 */
export function clearRegistry(): void {
    registry.clear();
}
